import { TimerReadNode } from "../timers/TimerReadNode";
import { ObjectDefinitionNode } from "./ObjectDefinitionNode";
import { ObjectConstructorNode } from "./ObjectConstructorNode";
import type {
  DatePartTextFormat,
  Port,
  VariableValueType,
  BinaryOperatorNode as BinaryOperatorNodeType,
  UnaryOperatorNode as UnaryOperatorNodeType,
  TernaryOperatorNode as TernaryOperatorNodeType,
} from "../../../../util/src/blueprint/types.js";
import type { BehaviorNode } from "../../../../util/src/blueprint/index.js";
import { BinaryOperatorNode } from "./BinaryOperatorNode.js";
import { UnaryOperatorNode } from "./UnaryOperatorNode.js";
import { TernaryOperatorNode } from "./TernaryOperatorNode.js";
import React from "react";

export interface OperatorNodeProps {
  node: BehaviorNode;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onPortValueChange?: (nodeId: string, portId: string, value: unknown) => void;
  onObjectPortsChange?: (
    nodeId: string,
    ports: Port[],
    renamed?: Record<string, string>,
  ) => void;
  onDatePartFormatChange?: (
    nodeId: string,
    format: DatePartTextFormat | undefined,
  ) => void;
  onListItemValueTypeChange?: (
    nodeId: string,
    valueType: Exclude<VariableValueType, "list">,
  ) => void;
  onObjectFieldPathChange?: (nodeId: string, fieldPath: string) => void;
  onTimerPortsChange?: (nodeId: string, ports: Port[]) => void;
}

const OperatorNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
  onObjectPortsChange,
  onDatePartFormatChange,
  onListItemValueTypeChange,
  onObjectFieldPathChange,
  onTimerPortsChange,
}: OperatorNodeProps) => {
  if (node.type !== "operator") {
    return null;
  }

  if (node.operator === "readTimer")
    return (
      <TimerReadNode
        node={node}
        setPortsMap={setPortsMap}
        onPortMouseDown={onPortMouseDown}
        onPortMouseUp={onPortMouseUp}
        isPortConnected={isPortConnected}
        onPortValueChange={onPortValueChange}
        onTimerPortsChange={onTimerPortsChange}
      />
    );

  if (node.operator === "constructObject" && node.objectDefinition)
    return (
      <ObjectDefinitionNode
        node={node}
        setPortsMap={setPortsMap}
        onPortMouseDown={onPortMouseDown}
        onPortMouseUp={onPortMouseUp}
        isPortConnected={isPortConnected}
        onPortValueChange={onPortValueChange}
        onObjectPortsChange={onObjectPortsChange}
      />
    );

  if (node.operator === "constructObject")
    return (
      <ObjectConstructorNode
        node={node}
        setPortsMap={setPortsMap}
        onPortMouseDown={onPortMouseDown}
        onPortMouseUp={onPortMouseUp}
        isPortConnected={isPortConnected}
        onPortValueChange={onPortValueChange}
      />
    );

  if (node.mode === "ternary") {
    return (
      <TernaryOperatorNode
        node={node as TernaryOperatorNodeType}
        setPortsMap={setPortsMap}
        onPortMouseDown={onPortMouseDown}
        onPortMouseUp={onPortMouseUp}
        isPortConnected={isPortConnected}
        onPortValueChange={onPortValueChange}
        onListItemValueTypeChange={onListItemValueTypeChange}
      />
    );
  }

  if (node.mode === "binary") {
    return (
      <BinaryOperatorNode
        node={node as BinaryOperatorNodeType}
        setPortsMap={setPortsMap}
        onPortMouseDown={onPortMouseDown}
        onPortMouseUp={onPortMouseUp}
        isPortConnected={isPortConnected}
        onPortValueChange={onPortValueChange}
        onListItemValueTypeChange={onListItemValueTypeChange}
        onObjectFieldPathChange={onObjectFieldPathChange}
      />
    );
  }

  return (
    <UnaryOperatorNode
      node={node as UnaryOperatorNodeType}
      setPortsMap={setPortsMap}
      onPortMouseDown={onPortMouseDown}
      onPortMouseUp={onPortMouseUp}
      isPortConnected={isPortConnected}
      onPortValueChange={onPortValueChange}
      onDatePartFormatChange={onDatePartFormatChange}
      onListItemValueTypeChange={onListItemValueTypeChange}
      onObjectFieldPathChange={onObjectFieldPathChange}
    />
  );
};

export const OperatorNode = React.memo(OperatorNodeComponent);
